import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { PHOTOS } from "../../data";
import { scrollToId } from "./Navbar";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.3 + i * 0.12, ease: [0.76, 0, 0.24, 1] } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const txtY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen overflow-hidden"
      style={{ background: "var(--navy)" }}>
      {/* animated gradient layers */}
      <div className="absolute inset-0 opacity-70"
        style={{ background: "radial-gradient(1000px circle at 15% 20%, rgba(17,34,64,0.9), transparent 45%), radial-gradient(800px circle at 85% 80%, rgba(255,153,51,0.08), transparent 40%)" }} />
      <div className="absolute inset-0"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)", backgroundSize: "70px 70px", maskImage: "radial-gradient(circle at 50% 40%, black, transparent 75%)" }} />
      <div className="karnataka-bg absolute inset-y-0 right-0 w-1/2 opacity-[0.10]" style={{ backgroundImage: "url(/photos/karnataka.svg)" }} aria-hidden />


      <div className="container-x relative z-10 grid lg:grid-cols-12 gap-10 items-center min-h-screen pt-28 pb-16">
        {/* LEFT */}
        <motion.div style={{ y: txtY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="flex items-center gap-3 mb-7">
            <span className="h-px w-10" style={{ background: "var(--gold)" }} />
            <span className="eyebrow" style={{ color: "var(--gold)" }}>Public Service • Leadership • Karnataka</span>
          </motion.div>

          <h1 className="font-display font-bold text-ivory tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
            {["Listening to People.", "Building for Tomorrow."].map((t, i) => (
              <span key={i} className="reveal-mask">
                <motion.span className="block" variants={line} custom={i} initial="hidden" animate="show"
                  style={{ color: i === 1 ? "var(--ivory)" : "var(--ivory)" }}>
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="font-body text-ivory/85 text-base md:text-lg leading-relaxed max-w-xl mt-7">
            Good leadership begins with understanding people — hearing what truly matters to them, and turning it into honest, everyday work.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4 mt-10">
            <button data-testid="hero-connect-btn" onClick={() => scrollToId("#contact")}
              className="btn-primary group inline-flex items-center gap-2 rounded-full font-body font-semibold px-7 py-3.5">
              Connect With Us <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button data-testid="hero-journey-btn" onClick={() => scrollToId("#journey")}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 text-ivory font-body font-medium px-7 py-3.5 hover:bg-white/5 hover:border-white/45 transition-all duration-300">
              Discover the Journey
            </button>
          </motion.div>
        </motion.div>

        {/* RIGHT */}
        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: imgY }}
            initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative spotlight">
            <div className="relative rounded-[20px] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
              <img src={PHOTOS.portrait} alt="Puttraj, entrepreneur and public leader from Karnataka" loading="eager" fetchpriority="high"
                className="w-full h-[420px] md:h-[560px] object-cover object-top" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(10,25,47,0.55), transparent 45%)" }} />
            </div>
            {/* floating glass label */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }}
              className="glass-dark rounded-2xl px-5 py-4 absolute -bottom-6 -left-6 max-w-[230px]">
              <div className="flex items-center gap-2">
                <img src="/photos/congress.png" alt="Indian National Congress" className="h-5 w-5 rounded-full object-contain" />
                <p className="eyebrow" style={{ color: "var(--gold)" }}>A Leader Who Listens</p>
              </div>
              <p className="font-display text-ivory text-sm mt-2 font-medium">Entrepreneur · Media Leader · Film Producer</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* scroll cue */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <span className="eyebrow text-ivory/75">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={18} color="var(--ivory)" />
        </motion.div>
      </motion.div>
    </section>
  );
};
